import Button from '@/app/components/common/Button'
import Pill from '@/app/components/common/Pill'
import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import HotelCardCarousel from '@/app/components/HotelCardCarousel'
import HotelListItemClick from '@/app/components/HotelListItemClick'
import { Icons } from '@/app/components/ui/Icons'
import {
	DEFAULT_USER_COUNTRY,
	INIT_DATA_STORAGE_KEY,
	KM_TO_MILES,
	SM_BREAKPOINT,
	UUID_NAMESPACE,
	XL_BREAKPOINT
} from '@/app/constants'
import '@/app/styles/components/HotelListItem.css'
import { HotelItem, HotelsListResponse, UserData } from '@/app/types'
import {
	getRatingColor,
	getRatingValue,
	isFromUS,
	isRtlLanguage,
	ratingsTranslations,
} from '@/app/utils'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { FC, PropsWithChildren, useState } from 'react'
import { v5 as uuid } from 'uuid'
import Stars from './Stars'
import Badge from './common/Badge'

const MAX_PRICE_LENGTH = 7

export interface HotelListItemProps extends PropsWithChildren {
	item: HotelItem
	index: number
	className?: string
	lang?: string
	isSingleHotel?: boolean
	isLandmark?: boolean
	fullMapView?: boolean
	isMapCard?: boolean
	searchParams: HotelsListResponse['searchParams']
}

const HotelListItemSmall: FC<HotelListItemProps> = (props) => {
	const {
		item,
		index,
		lang,
		isSingleHotel,
		searchParams,
		isLandmark,
		className,
		children,
		isMapCard,
		fullMapView,
	} = props
	const t = useTranslations('Keywords')
	const tRating = useTranslations('guestRatings')
	const hasAvailability = item.rawHotelData.bestRate.availableRooms > 0
	const isRtl = isRtlLanguage(lang || 'en')
	const [isRatesExpanded, setIsRatesExpanded] = useState(false)
	const isLargeDesktop = useMediaQuery(XL_BREAKPOINT)
	const isMobile = useMediaQuery(SM_BREAKPOINT)

	const [userData, _] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const userCountry = userData?.userCountry || DEFAULT_USER_COUNTRY
	const slides = [
		item.largeImage || item.image || '',
		...(item.rawHotelData.moreImages
			?.slice(0, 4)
			.map((image) => image.small || image.large || '') || []),
	].filter(Boolean)

	const toggleRates = () => {
		setIsRatesExpanded(!isRatesExpanded)
	}

	const renderRates = () => {
		const rates = item.rates.slice(0, 3) // Show maximum 3 rates

		return rates.map((rate, i) => (
			<div
				key={rate.bookURI + i}
				className={classNames('hotel-list-item__rate', {
					'md:col-start-3': rates.length === 1,
					'md:col-start-2': rates.length === 2 && i === 0,
				})}
			>
				<HotelListItemClick
					passthruLink={`/${lang}/passthru?uid=${uuid(
						rate.bookURI,
						UUID_NAMESPACE
					)}`}
					link={rate.bookURI}
					provider={rate.providerName}
					item={item}
					searchParams={searchParams}
					isAdditionalPrice
					price={rate.rawPrice}
				/>
				<Image
					loading={'lazy'}
					width={60}
					height={12}
					style={{ height: 'auto' }}
					src={rate.provider.logo}
					alt={`${rate.provider.logo} image`}
					unoptimized
					className="md:mx-auto"
				/>
				<Text color={null} font={null} className="text-xs">
					{rate.nightlyRate} <span className='!font-normal'>/{t('perNight')}</span>
				</Text>
			</div>
		))
	}

	const renderRating = () => {
		const { rating, numberOfReviews } = item;
		const mappedRating = tRating(ratingsTranslations(rating));

		return (
			!!rating && (
				<div className="hotel-list-item__rating">
					<>
						<div
							className="hotel-list-item__rating-badge max-lg:hidden"
							data-test-id="hotel-list-item__rating"
						>
							{/* desktop rating */}
							<Badge bgColor="secondary" textColor="white">
								{getRatingValue(rating)}
							</Badge>
						</div>
					</>
					{mappedRating && (
						<div className="hotel-list-item__rating-text pl-[4px]">
							<div className="w-full flex flex-row">
								<div
									className={`text-secondary font-semibold mr-2 rtl:mr-0 rtl:ml-2 inline text-sm me-1 hidden`}
								>
									{getRatingValue(rating)}
								</div>
								<Text
									tag="div"
									font="semibold"
									size="sm"
									className={classNames(
										'leading-5 inline order-1 w-full rtl:mr-0 rtl:ml-1',
										`text-secondary`
									)}
								>
									{mappedRating}
								</Text>
							</div>
							{numberOfReviews > 0 && (
								<Text
									size="xs"
									className="text-text-600 order-3 md:order-2 max-md:ms-1 px-1 whitespace-nowrap"
								>
									({numberOfReviews} {t('reviews')})
								</Text>
							)}
						</div>
					)}
				</div>
			)
		)
	}

	const distanceInKm = Number((item.distanceFromCenter / 1000).toFixed(1))

	const getDistanceFromCenter = () => {
		if (distanceInKm < 1) {
			return t('inCityCenter')
		}

		const newDistance = distanceInKm - 1
		if (newDistance < 0.2) {
			return t('inCityCenter')
		}
		const isUS = isFromUS(userCountry)
		const unit = isUS ? 'mi' : 'km'
		const convertedDistance = isUS ? newDistance * KM_TO_MILES : newDistance
		if (isRtl) {
			const isHe = lang === 'he';
			return `\u200F ${Number(convertedDistance).toFixed(1)} ${isHe ? 'ק"מ' : unit} ${t(
				'fromTheCenter'
			)} `
		}
		return `${Number(convertedDistance).toFixed(1)} ${unit} ${t(
			'fromTheCenter'
		)}`
	}

	const renderLocation = () => {
		const { location, distanceFromCenter } = item
		const locationInfo = location || distanceFromCenter

		if (!locationInfo) return null

		return (
			<div className="hotel-list-item__location">
				<div className="hotel-list-item__location-content">
					<Text size="xs" color={'text-800'}>
						<span>{location}</span>
						{!isLandmark && (
							<span>
								{location && ' · '}
								{getDistanceFromCenter()}
							</span>
						)}
					</Text>
				</div>
			</div>
		)
	}

	const renderRedTexts = () => {
		return null
	}

	const renderGreenTexts = () => {
		const data = item.rawHotelData.bestRate
		const { hasFreeCancellation, canPayLater } = data

		return (
			<div className="flex flex-wrap gap-2">
				{hasFreeCancellation && (
					<Pill color="success-950" background="success-100">
						{t('freeCancellation')}
					</Pill>
				)}
				{canPayLater && (
					<Pill color="success-950" background="success-100">
						{t('noPrepayment')}
					</Pill>
				)}
			</div>
		)
	}
	const MAX_TITLE_LENGTH = 60

	const renderTitle = () => {
		const titleToRender = isRtl
			? item.translatedName || item.title
			: item.title
		const isTitleTooLong = titleToRender.length > MAX_TITLE_LENGTH

		return (
			<div className="flex lg:justify-between flex-col">
				<Stars size="12" count={item.stars} className="inline-flex items-center mb-1 " />
				<Title
					tag="h3"
					level="3"
					bold
					fontSize="2xl"
					data-test-id="hotel-list-item__title"
					className={classNames('hotel-list-item__title', {
						'hotel-list-item__title--arabic':
							isRtl && item.translatedName === item.title,
					})}
					title={isTitleTooLong ? titleToRender : undefined} // Show full title on hover if truncated
				>
					<span className='mr-3'>{isTitleTooLong
						? `${titleToRender.substring(0, MAX_TITLE_LENGTH).trim()}...`
						: titleToRender}</span>
				</Title>
			</div>
		)
	}

	const renderBestPriceOn = () =>
		item.rawHotelData.bestRate.provider.logo ? (
			<>
				{(isLargeDesktop && !isMapCard) && <Text size="xs" className="me-1.5" color="text-700">
					{t('bestPriceOnLogo')}
				</Text>}
				<Image
					loading={'lazy'}
					width={60}
					height={16}
					src={item.rawHotelData.bestRate.provider.logo}
					alt={`${item.rawHotelData.bestRate.provider.logo} image`}
					quality={100}
					className="hotel-list-item__best-price-logo"
					style={{ objectFit: 'contain' }}
				/>
			</>
		) : null


	const renderViewDeal = () => {
		const isPriceToLong = item.price.length > MAX_PRICE_LENGTH

		return (
			<div className="hotel-list-item__sm-view-deal-and-rates">
				<div className="hotel-list-item__sm-view-deal">
					<div className="hotel-list-item__devider w-full h-[1px] bg-text-200 mb-3"></div>
					{!isMapCard && (
						<HotelListItemClick
							passthruLink={`/${lang}/passthru?uid=${uuid(
								item.link,
								UUID_NAMESPACE
							)}`}
							link={item.link}
							provider={item.provider}
							item={item}
							searchParams={searchParams}
							price={item.rawPrice}
						/>
					)}
					<div
						className={classNames('hotel-list-item__sm-price-container', {
							'hotel-list-item__price-container--long': isPriceToLong,
							isMapCard
						})}
					>
						<div className={classNames("flex flex-col", {
							"w-full !flex-row-reverse justify-between items-center": isMapCard,
						})} >
							<div className="flex justify-start items-center">
								<Text
									tag="div"
									size="2.7xl"
									font="bold"
									className="hotel-list-item__sm-price"
								>
									{item.price}
								</Text>
								<Text className="hotel-list-item__per-night">
									/{t('perNight')}
								</Text>
							</div>
							<div className="hotel-list-item__best-price">
								{renderBestPriceOn()}
							</div>
							{!isMapCard && <Text tag="p" size="xs" color="text-700" className="mt-1">
								{t('taxes')}
							</Text>}
						</div>
						<Button
							bgColor="main"
							textColor="white"
							name="View Deal"
							className={classNames("hotel-list-item__view-deal-button relative z-10 pointer-events-none", { 'small': lang === 'pl' || lang === 'tr' })}
						>
							{t('viewDeal')}
						</Button>
					</div>
				</div>
				{
					item.rates.length > 0 && !isMapCard && (
						<div className="hotel-list-item__sm-rates">
							<div
								className="text-sm font-bold flex justify-center items-center pb-2"
								onClick={toggleRates}
							>
								{isRatesExpanded ? t('hide') : t('morePrices')}
								<Icons.caret
									className={classNames('ms-2 transition-transform', {
										'rotate-180': isRatesExpanded,
									})}
								/>
							</div>
							<AnimatePresence>
								{isRatesExpanded && (
									<motion.div
										initial={{ height: 0, opacity: 0 }}
										animate={{ height: 'auto', opacity: 1 }}
										exit={{ height: 0, opacity: 0 }}
										transition={{ duration: 0.2 }}
										className="overflow-hidden"
									>
										{renderRates()}
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					)
				}
			</div >
		)
	}

	return (
		<div
			className={classNames('hotel-list-item small', className, {
				'mt-0': isSingleHotel || index === 0,
				'map-card': isMapCard,
				'compact-map-card': !isLargeDesktop
			})}
			data-test-id="hotel-item"
			id={`hotel-${item.id}`}
		>
			<div className="hotel-list-item__inner">
				{/* image */}
				<div className="hotel-list-item__images-container">
					<HotelCardCarousel key={index} slides={slides} />
				</div>

				{/* content */}
				<div className="hotel-list-item__content">
					<div className="hotel-list-item__main-content">
						{/* header */}
						<div className="hotel-list-item__header">
							{renderTitle()}
							{!isMapCard && <div className="hotel-list-item__location-wrapper">
								{renderLocation()}
							</div>}
							<div className="hotel-list-item__rating--mobile">
								{renderRating()}
							</div>
							{/* notice */}
							{!isMapCard && <div className="hotel-list-item__notices">
								{renderRedTexts()}
								{renderGreenTexts()}
							</div>}
						</div>
						{hasAvailability ? renderViewDeal() : (
							<div className={classNames("mb-4 relative", { "!mb-1": isMapCard })}>
								<div className={classNames("w-full h-[1px] bg-text-200 mb-3", { "!mb-0": isMapCard })} />
								{isMapCard ? (
									<>
										<HotelListItemClick
											passthruLink={`/${lang}/passthru?uid=${uuid(
												item.link,
												UUID_NAMESPACE
											)}`}
											link={item.link}
											provider={item.provider}
											item={item}
											searchParams={searchParams}
											price={item.rawPrice}
										/>
										<Button
											textColor="dark-blue"
											fontSize="base"
											name="View Deal"
											className="!z-0 !px-0"
										>
											<span className="flex items-center font-bold justify-center w-full">
												{t('seeAvailability')}
												<Icons.iconArrowSmall className="[&_path]:stroke-dark-blue ms-2 rtl:scale-x-[-1]" />
											</span>
										</Button>
									</>
								) : (
									<>
										<div
											className="flex justify-start items-center text-text-600 text-xs mb-3"
											data-test-id="no-room-available"
										>
											<span>{isMobile && fullMapView ? t('noRoomsTryDates') : t('singleHotelNotFound')}</span>
										</div>
										<HotelListItemClick
											passthruLink={`/${lang}/passthru?uid=${uuid(
												item.link,
												UUID_NAMESPACE
											)}`}
											link={item.link}
											provider={item.provider}
											item={item}
											searchParams={searchParams}
											price={item.rawPrice}
										/>

										{isMobile && fullMapView ? (
											<Button
												textColor="dark-blue"
												fontSize="base"
												name="View Deal"
												className="!z-0 !p-0"
											>
												<span className="flex items-center font-bold justify-center w-full">
													{t('seeAvailability')}
													<Icons.iconArrowSmall className="[&_path]:stroke-dark-blue ms-2 rtl:scale-x-[-1]" />
												</span>
											</Button>
										) : (
											<Button
												bgColor="main"
												textColor="white"
												fontSize="base"
												name="View Deal"
												className="hotel-list-item__view-deal-button !z-0"
											>
												<span className="flex items-center justify-center w-full">
													{t('seeAvailability')}
													<Icons.iconArrowSmall className="ms-2 rtl:scale-x-[-1]" />
												</span>
											</Button>)}
									</>
								)}
							</div>
						)}
					</div>
					{isMapCard && (
						<div className='z-20'>
							<HotelListItemClick
								passthruLink={`/${lang}/passthru?uid=${uuid(
									item.link,
									UUID_NAMESPACE
								)}`}
								link={item.link}
								provider={item.provider}
								item={item}
								searchParams={searchParams}
								price={item.rawPrice}
							/>
						</div>
					)}
				</div>
			</div>
			{children}
		</div>
	)
}

export default HotelListItemSmall
